/**
 * Keeps one broken component from taking the whole page with it.
 *
 * React unmounts the entire tree when a render or lifecycle method throws, so
 * without a boundary a fault anywhere — a dropdown, one table cell — leaves the
 * operator on a white screen with no message and no recovery but a reload. A
 * broken filter should cost the filter, not the audit.
 *
 * Reporting was already solved: `utils/usageTracking.ts` sends uncaught errors
 * and unhandled rejections to the backend EventLog, which feeds the Error Alert
 * email. A boundary CATCHES the throw, which means the window listener never
 * sees it — so this reports through the same `recordError` path deliberately,
 * or catching the crash would have made it quieter than leaving it uncaught.
 *
 * A boundary only sees render and lifecycle errors. Failures inside event
 * handlers and promise rejections still go through usageTracking's global
 * hooks; the two halves are complementary and neither replaces the other.
 */
import React from "react"

import { recordError } from "../api/events"
import { THEME } from "../config/theme"

interface Props {
  children: React.ReactNode
  /** Names the surface in the alert e.g. "worklist" — not shown to the operator. */
  surface: string
  /** Replaces the default panel when a caller wants a different shape. */
  fallback?: (retry: () => void) => React.ReactNode
}

interface State {
  error: Error | null
}

const panelStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: THEME.spacing.sm,
  flexWrap: "wrap",
  padding: THEME.spacing.md,
  margin: `${THEME.spacing.sm} 0`,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.white,
  fontFamily: THEME.fonts.primary,
  fontSize: 12.5,
  color: THEME.colors.neutral.light.textPrimary,
}

// Matches the worklist group's Retry button (WorklistGroup.tsx retryButtonStyle),
// the other place the operator is offered a second attempt at a failed panel.
const retryButtonStyle: React.CSSProperties = {
  padding: "4px 10px",
  fontSize: 12,
  fontFamily: THEME.fonts.primary,
  borderRadius: THEME.borderRadius.sm,
  border: `1px solid ${THEME.colors.silver}`,
  background: THEME.colors.white,
  color: THEME.colors.neutral.light.textPrimary,
  cursor: "pointer",
}

export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Same pipeline the global handlers use, so a caught crash still reaches
    // the Error Alert email rather than being silently swallowed here.
    recordError({
      event_type: "client_error",
      path: window.location.pathname,
      message: `[${this.props.surface}] ${error.message || "Render error"}${
        info.componentStack ? ` | ${info.componentStack.trim().split("\n")[0]}` : ""
      }`.slice(0, 500),
    })
  }

  private retry = (): void => {
    this.setState({ error: null })
  }

  render(): React.ReactNode {
    if (this.state.error === null) return this.props.children
    if (this.props.fallback) return this.props.fallback(this.retry)

    return (
      <div style={panelStyle} role="alert">
        <span>
          This section couldn&rsquo;t be displayed. The rest of the page still works.
        </span>
        <button type="button" onClick={this.retry} style={retryButtonStyle}>
          Try again
        </button>
      </div>
    )
  }
}
